// Resolving KPI value-indicators against the Metabase-backed query catalog.
//
// An indicator names a query-catalog entry id + a reducer. We run the query
// (live when a Metabase source is bound, sample otherwise) and reduce the
// result rows to one headline. Column convention across the catalog: the
// first column is the label, the last numeric column is the value — so we
// don't need the chart spec here.

import { runCatalogQuery, type QueryRunResult } from "./query-catalog";
import type { Reducer, ValueIndicator, ValueUnit } from "./kpis";

export interface ResolvedValue {
  /** "live" (Metabase), "sample" (catalog fallback), "awaiting" (no query),
   *  or "error". */
  state: "live" | "sample" | "awaiting" | "error";
  /** The headline string to render (without unit suffix when `unit` is set). */
  text: string;
  /** Semantic unit when the indicator declared one (coins / currency / etc). */
  unit?: ValueUnit;
  /** Source label from the query run, for the live/sample tag. */
  source?: string;
  error?: string;
}

function valueColIndex(columns: string[]): number {
  // last column is the value by catalog convention; guard for empty.
  return columns.length > 0 ? columns.length - 1 : 0;
}

function toNum(v: string | number | null): number | null {
  if (typeof v === "number") return v;
  if (typeof v === "string") {
    const n = Number(v.replace(/,/g, ""));
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

function fmt(n: number): string {
  return n.toLocaleString("en-US", { maximumFractionDigits: 0 });
}

function reduce(res: QueryRunResult, reducer: Reducer, suffix?: string): string {
  const vi = valueColIndex(res.columns);
  const rows = res.rows;
  const suf = suffix ? ` ${suffix}` : "";

  switch (reducer) {
    case "rowCount":
      return `${fmt(rows.length)}${suf}`;
    case "firstRowValue": {
      const r = rows[0];
      const n = r ? toNum(r[vi] ?? null) : null;
      return n === null ? "—" : `${fmt(n)}${suf}`;
    }
    case "lastRowValue": {
      const r = rows[rows.length - 1];
      const n = r ? toNum(r[vi] ?? null) : null;
      return n === null ? "—" : `${fmt(n)}${suf}`;
    }
    case "sumValue": {
      let total = 0;
      for (const r of rows) {
        const n = toNum(r[vi] ?? null);
        if (n !== null) total += n;
      }
      return `${fmt(total)}${suf}`;
    }
    case "topLabel": {
      // label = first column; pick the row with the max value.
      let best: { label: string; val: number } | null = null;
      for (const r of rows) {
        const n = toNum(r[vi] ?? null);
        const label = r[0];
        if (n !== null && (best === null || n > best.val)) {
          best = { label: label == null ? "—" : String(label), val: n };
        }
      }
      return best ? best.label : "—";
    }
    default:
      return "—";
  }
}

export async function resolveIndicator(ind: ValueIndicator): Promise<ResolvedValue> {
  if (!ind.queryId) {
    return { state: "awaiting", text: ind.placeholder ?? "awaiting query", unit: ind.unit };
  }
  try {
    const res = await runCatalogQuery(ind.queryId);
    if (res.status === "error") {
      return {
        state: "error",
        text: ind.placeholder ?? "query error",
        unit: ind.unit,
        error: res.error ?? "unknown error",
        source: res.source_label,
      };
    }
    // When a semantic unit is set, the chip carries the unit — don't append.
    const suffix = ind.unit ? undefined : ind.suffix;
    const text = reduce(res, ind.reducer ?? "firstRowValue", suffix);
    return {
      state: res.status === "live" ? "live" : "sample",
      text,
      unit: ind.unit,
      source: res.source_label,
    };
  } catch (e) {
    return { state: "error", text: ind.placeholder ?? "query error", unit: ind.unit, error: String(e) };
  }
}
